/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Card,
  Chip,
  Grid,
  Link,
  Typography,
  useTheme,
  Skeleton,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useEffect, useMemo, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {useInView} from '@jobseeker/lotus/hooks/observer';
import type {
  AdsElementType,
  AdsEventInfo,
  AppState,
} from '@jobseeker/common-core/types';
import {
  getUbaPageName,
  handleTracking,
  parseShowcaseUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {shallowEqual, useSelector} from 'react-redux';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import SlideArrows from '@jobseeker/lotus/components/SlideArrows';
import {useMediaQuery} from '@mui/material';

export type GoldPartnersProps = {
  label?: string;
  titleOut?: boolean;
  adsEventInfo?: AdsEventInfo;
  slot: string;
};

const BORDER = {
  borderRadius: '16px',
  border: '1px solid',
  borderColor: 'border.main',
  background: '#FFF',
  boxShadow: '0 1.2px 0 0 #F3F7FA',
};

const GoldPartners = ({
  label = 'Hiring Partners',
  titleOut = false,
  adsEventInfo,
  slot,
}: GoldPartnersProps) => {
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const theme = useTheme();
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const [fetchedAds, setFetchedAds] = useState(Array<AdsElementType>);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAdsLoading, setIsAdsLoading] = useState(true);
  const device = useSelector(
    (state: AppState) => state.app.layout.device,
    shallowEqual
  );
  const isMobile = device === 'mobile';
  const shouldLoad = Boolean(slot);
  useLoadAds(
    slot || '',
    setFetchedAds,
    {},
    false,
    shouldLoad,
    (loaded: boolean) => setIsAdsLoading(!loaded),
    shouldLoad
  );

  const allAds: AdsElementType[] = useMemo(() => {
    const items = (fetchedAds || []).filter(Boolean);
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [fetchedAds]);
  const slides: AdsElementType[][] = useMemo(() => {
    const pages: AdsElementType[][] = [];
    // Show 2 ads per slide on mobile, 8 on desktop
    const adsPerSlide = isMobile ? 2 : 8;

    for (let i = 0; i < allAds.length; i += adsPerSlide) {
      pages.push(allAds.slice(i, i + adsPerSlide));
    }
    return pages;
  }, [allAds, isMobile]);

  useEffect(() => {
    setCurrentSlide(0);
  }, [slides.length]);

  const trackGoldPartnersEvent = (args: {
    type: 'view' | 'click';
    companyId?: string | number;
    companyName?: string;
    custId?: string;
    quoteLineNo?: string;
    centralisedCompanyId?: string;
    items?: AdsElementType[];
  }) => {
    const {
      type,
      companyId,
      companyName,
      custId,
      quoteLineNo,
      centralisedCompanyId,
      items,
    } = args;
    try {
      const refParam =
        new URL(window.location.href).searchParams.get('ref') || '';
      const source = 'Logo';
      let companyIDSArray: Array<string | number> = [];
      let companyNamesArray: string[] = [];
      let custIdsArray: Array<string | undefined> = [];
      let quoteLineNoArray: Array<string | undefined> = [];
      let centralCompanyIdsArray: Array<string | undefined> = [];
      if (type === 'view') {
        const list = items || allAds;
        companyIDSArray = list
          .map(ad => ad?.companyId || parseShowcaseUrl(ad?.link))
          .filter(Boolean) as Array<string | number>;
        companyNamesArray = list
          .map(ad => ad?.title)
          .filter(Boolean) as string[];
        custIdsArray = list.map(ad => ad?.custId);
        quoteLineNoArray = list.map(ad => ad?.quoteLineNo);
        centralCompanyIdsArray = list.map(ad => ad?.centralisedCompanyId);
      }
      const hasQuoteLineNo = quoteLineNoArray.some(line => line != null);
      const hasCustId = custIdsArray.some(line => line != null);
      const hasCentralCompanyId = centralCompanyIdsArray.some(
        line => line != null
      );
      const payload =
        type === 'view'
          ? {
              ref: refParam,
              source,
              companyIDSArray,
              companyNamesArray,
              ...(hasCustId && {custIdsArray}),
              ...(hasQuoteLineNo && {quoteLineNoArray}),
              ...(hasCentralCompanyId && {centralCompanyIdsArray}),
              page: getUbaPageName(),
              sessionUsername: `${sessionStorage.getItem('userId')}`,
            }
          : {
              ref: refParam,
              source,
              companyID: companyId || '',
              companyName: companyName || '',
              ...(custId != null && {custId}),
              ...(quoteLineNo != null && {quoteLineNo}),
              ...(centralisedCompanyId != null && {
                centralCompanyID: centralisedCompanyId,
              }),
              page: getUbaPageName(),
              sessionUsername: `${sessionStorage.getItem('userId')}`,
            };

      TrackEventUBA(
        type === 'view' ? 'companyBannerView' : 'companyBannerClick',
        type,
        payload
      );
    } catch (e) {
      console.error('UBA Event Tracking Error', e);
    }
  };

  useEffect(() => {
    if (!hasTrackedView && allAds.length) {
      trackGoldPartnersEvent({type: 'view', items: allAds});
      setHasTrackedView(true);
    }
  }, [allAds, hasTrackedView]);

  if (!allAds?.length && !isAdsLoading) return null;

  return (
    <>
      {titleOut && (
        <Box display="flex" alignItems="center" sx={{mb: 1.5}}>
          <Typography
            fontSize={isAppIimjobs ? {xs: 16, md: 14} : 16}
            fontWeight={isAppIimjobs ? 500 : 600}
            color="secondary.main"
          >
            {label}
          </Typography>
          {slides.length > 1 && (
            <Box sx={{ml: 'auto'}}>
              <SlideArrows
                scrollLeft={() => setCurrentSlide(s => Math.max(0, s - 1))}
                scrollRight={() =>
                  setCurrentSlide(s => Math.min(slides.length - 1, s + 1))
                }
                disableLeft={currentSlide === 0}
                disableRight={currentSlide === slides.length - 1}
              />
            </Box>
          )}
        </Box>
      )}

      <Card
        sx={{
          borderRadius: titleOut ? 2 : 4,
          px: {xs: 1, sm: 1.5},
          py: {xs: 1.5, sm: 2},
          mb: titleOut ? 3.75 : 2,
          minWidth: {sm: 368, md: 'auto'},
          minHeight: {sm: 211, md: 'auto'},
        }}
        data-testid={`job_ads_banner_${label
          ?.toLowerCase()
          ?.replace(/ /g, '-')}`}
        id={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
      >
        {!titleOut && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{mb: 2}}
          >
            <Typography fontSize={14} fontWeight={500} color="secondary.main">
              {label}
            </Typography>
            {slides.length > 1 && (
              <Box sx={{ml: 'auto'}}>
                <SlideArrows
                  scrollLeft={() => setCurrentSlide(s => Math.max(0, s - 1))}
                  scrollRight={() =>
                    setCurrentSlide(s => Math.min(slides.length - 1, s + 1))
                  }
                  disableLeft={currentSlide === 0}
                  disableRight={currentSlide === slides.length - 1}
                />
              </Box>
            )}
          </Box>
        )}

        <AnimatePresence mode="wait">
          {isAdsLoading ? (
            <motion.div
              key="gold-partners-skeleton"
              initial={{opacity: 1}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              transition={{duration: 0.2}}
            >
              <Grid container spacing={{xs: 1, sm: 2}}>
                {Array.from({length: isMobile ? 2 : 6}).map((_, idx) => (
                  <Grid
                    item
                    xs={6}
                    sm={isMobile ? 6 : 6}
                    key={`skeleton-${idx}`}
                  >
                    <Box
                      sx={{
                        ...BORDER,
                        p: 1.25,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection={'column'}
                        gap={1.5}
                        alignItems="flex-start"
                      >
                        <Skeleton
                          variant="rectangular"
                          width={{xs: 32, sm: 50}}
                          height={{xs: 32, sm: 50}}
                          sx={{borderRadius: '16px'}}
                        />
                        <Box flex={1} minWidth={0} width="100%">
                          <Skeleton
                            variant="text"
                            width="80%"
                            height={{xs: 12, sm: 16}}
                            sx={{mb: 0.5}}
                          />
                          <Skeleton
                            variant="text"
                            width="60%"
                            height={{xs: 10, sm: 12}}
                            sx={{mb: 0.5}}
                          />
                          <Skeleton
                            variant="text"
                            width="50%"
                            height={{xs: 10, sm: 12}}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          ) : (
            <motion.div
              key="gold-partners-content"
              initial={{opacity: 0, y: -10}}
              animate={{opacity: 1, y: 0}}
              exit={{opacity: 0, y: 10}}
              transition={{duration: 0.15}}
            >
              <Box sx={{position: 'relative', overflow: 'hidden'}}>
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    transform: `translateX(-${currentSlide * 100}%)`,
                    transition: 'transform 300ms ease',
                  }}
                >
                  {slides.map((page, pageIndex) => (
                    <Box
                      key={`gold-partners-slide-${pageIndex}`}
                      sx={{flex: '0 0 100%', minWidth: '100%'}}
                    >
                      <Grid container spacing={{xs: 1, sm: 2}}>
                        {page.map((item, idx) => {
                          const imgUrl = item?.companyLogo || item?.imageUrl;
                          const companyId =
                            item?.companyId ||
                            parseShowcaseUrl(item?.link) ||
                            '';
                          const subtitle =
                            item?.subText || item?.description || '';
                          // const badgeText =
                          //   item?.ctaText &&
                          //   item.ctaText.toLowerCase().includes('role')
                          //     ? item.ctaText
                          //     : (item as any)?.badge;

                          return (
                            <Grid
                              item
                              xs={6}
                              sm={6}
                              key={`gold-partner-${pageIndex}-${idx}`}
                            >
                              <Box sx={{position: 'relative'}}>
                                {/* {badgeText && (
                                  <Chip
                                    label={badgeText}
                                    color="info"
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      zIndex: 1,
                                      backgroundColor: theme.palette.info.light,
                                      color: theme.palette.info.dark,
                                      fontWeight: 600,
                                      borderRadius: '10px',
                                    }}
                                  />
                                )} */}

                                <Box
                                  sx={{
                                    ...BORDER,
                                    p: 1.25,
                                    marginLeft: 0,
                                    backgroundColor: 'background.paper',
                                    transition: 'background-color 200ms ease',
                                    '&:hover': {
                                      backgroundColor: 'border.main',
                                    },
                                  }}
                                >
                                  <Link
                                    href={item?.link || '#'}
                                    target="_blank"
                                    rel="nofollow"
                                    underline="none"
                                    onClick={() => {
                                      trackGoldPartnersEvent({
                                        type: 'click',
                                        companyId,
                                        companyName: item?.title,
                                        custId: item?.custId,
                                        quoteLineNo: item?.quoteLineNo,
                                        centralisedCompanyId:
                                          item?.centralisedCompanyId,
                                      });
                                    }}
                                    sx={{display: 'block'}}
                                  >
                                    <Box
                                      display="flex"
                                      flexDirection={'column'}
                                      gap={1.5}
                                      alignItems="flex-start"
                                    >
                                      <Box
                                        sx={{
                                          ...BORDER,
                                          overflow: 'hidden',
                                          width: 50,
                                          height: 50,
                                          position: 'relative',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {imgUrl && (
                                          <Image
                                            src={imgUrl}
                                            alt={item?.title}
                                            fill
                                            style={{
                                              objectFit: 'contain',
                                            }}
                                            quality={100}
                                            blurDataURL={imgUrl}
                                            sizes="50px"
                                          />
                                        )}
                                      </Box>

                                      <Box flex={1} minWidth={0}>
                                        <Typography
                                          fontSize={12}
                                          fontWeight={700}
                                          color="secondary.main"
                                          noWrap
                                        >
                                          {item?.title}
                                        </Typography>
                                        {subtitle && (
                                          <Typography
                                            fontSize={10}
                                            fontWeight={400}
                                            lineHeight={1.2}
                                            color="text.secondary"
                                            sx={{
                                              display: '-webkit-box',
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: 'vertical',
                                              overflow: 'hidden',
                                            }}
                                          >
                                            {subtitle}
                                          </Typography>
                                        )}
                                        <Typography
                                          fontSize={10}
                                          fontWeight={700}
                                          color="primary.main"
                                          sx={{mt: 0.5}}
                                        >
                                          {item?.ctaText ||
                                            'Explore Office Culture'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Link>
                                </Box>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </>
  );
};

export default GoldPartners;
